import {
  Fragment,
  type ReactNode,
  type ComponentProps,
  type ElementType,
  useEffect,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XCircleIcon } from "icons/outline";
import { PolymorphicComponent } from "../utils/types";
import { cn } from "ui";

type DrawerProps = {
  isOpen: boolean;
  onClose(): void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: "max-w-xs" | "max-w-md" | "max-w-lg" | "max-w-xl" | "max-w-none";
  className?: string;
};

export default function Drawer({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = "max-w-md",
  className,
}: DrawerProps) {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-hidden"
        onClose={onClose}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-500 sm:duration-700"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-500 sm:duration-700"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-foundations-subtler/50" />
          </Transition.Child>

          <div
            className={cn(
              "fixed inset-y-0 right-0 flex max-w-full pl-16",
              className
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className={`w-screen ${size}`}>
                <div className="z-30 flex h-full flex-col bg-foundations-default shadow-xl">
                  <DrawerTitle
                    title={title}
                    description={description}
                    onClose={onClose}
                  />
                  {children}
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

const DrawerTitle = ({
  title,
  description,
  onClose,
}: {
  title: DrawerProps["title"];
  description?: DrawerProps["description"];
  onClose: DrawerProps["onClose"];
}) => (
  <div className="border-b border-b-foundations-subtle bg-foundations-subtelst-02 p-8 pr-6">
    <div className="flex justify-between">
      <div className="flex flex-col space-y-2">
        <Dialog.Title className="text-lg font-medium text-foundations-boldest">
          {title}
        </Dialog.Title>
        {description && (
          <Dialog.Description className="text-sm text-foundations-subtle">
            {description}
          </Dialog.Description>
        )}
      </div>
      <div className="ml-3 flex h-7 items-center">
        <button
          type="button"
          className="rounded-md text-foundations-subtler hover:text-foundations-subtle focus:outline-none"
          onClick={onClose}
        >
          <span className="sr-only">Close panel</span>
          <XCircleIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
);

const DrawerContent = <TTag extends ElementType = "div">({
  as,
  className = "",
  ...props
}: PolymorphicComponent<TTag>) => {
  const Component = as || "div";

  useEffect(() => {
    if (!window.Pylon) return;
    window.Pylon("hideChatBubble");
    return () => window.Pylon("showChatBubble");
  }, []);

  return (
    <Component
      className={`flex flex-1 flex-col overflow-y-auto ${className}`}
      {...props}
    />
  );
};

const DrawerFooter = ({ className = "", ...props }: ComponentProps<"div">) => (
  <div
    className={`flex justify-end  space-x-4 border-t border-t-foundations-subtle bg-foundations-default px-6 py-5 ${className}`}
    {...props}
  />
);

Drawer.Content = DrawerContent;
Drawer.Footer = DrawerFooter;
